import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CommunityPageSectionRow } from '@/types/cms';
import { useToast } from '@/hooks/use-toast';

export const useSectionReorder = (communityId: string | undefined, pageType: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (sections: CommunityPageSectionRow[]) => {
      if (!communityId) throw new Error('No community ID provided');

      // Update display_order for each section individually
      const updatePromises = sections.map((section, index) => {
        return supabase
          .from('community_page_sections')
          .update({ display_order: index + 1 })
          .eq('id', section.id);
      });

      const results = await Promise.all(updatePromises);
      
      // Check if any updates failed
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        console.error('Error updating section order:', errors);
        throw new Error('Failed to update section order');
      }

      return sections;
    },
    onSuccess: (sections) => {
      // Invalidate and refetch the community pages query
      queryClient.invalidateQueries({ 
        queryKey: ['admin-community-pages', communityId, pageType] 
      });

      // Optimistically update the cache
      queryClient.setQueryData(['admin-community-pages', communityId, pageType], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          sections: sections
        };
      });

      toast({
        title: "Sections reordered",
        description: "The section order has been updated successfully.",
      });
    },
    onError: (error) => {
      console.error('Error reordering sections:', error);
      toast({
        title: "Error",
        description: "Failed to reorder sections. Please try again.",
        variant: 'destructive',
      });
    }
  });
};